import * as THREE from "three";
import { generateUUID } from "three/src/math/MathUtils";
import { BaseManager } from "../base/BaseManager";
import { Context } from "../core/context";
import { IndserObject, IndsertPosition } from "../core/IndserObject";
import { MLayer } from "../core/MLayer";
import {
  DrawOperateState,
  IDrawOperation,
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
} from "../draws/IDrawOperation";
import { DrawOperationLine } from "../draws/DrawOperationLine";
import {
  DBEntity,
  DbLayer,
  ModelManager,
  ViewManager,
  DbLine,
} from "../CadEngine";
import { EventManager } from "./EventManager";
import { SelectionManager, Selection_Event } from "./SelectionManager";
import { DrawOperationCircle } from "../draws/DrawOperationCircle";
import { DrawOperationArc } from "../draws/DrawOperationArc";
import {
  DrawOperationEllipse,
  EllipseReturnData,
} from "../draws/DrawOperationEllipse";
import { DrawOperationEllipseArc } from "../draws/DrawOperationEllipseArc";
import { DrawLayout } from "@/draws/DrawLayout/DrawLayout";
import { DrawOperationRectangle } from "@/draws/DrawOperationRectangle";
import {
  CommonColor,
  DrawLayoutParams,
  DrawWallAcceptParams,
} from "@/core/Parameters";
import { DrawOperationWall } from "@/draws/DrawOperationWall";
import { DrawOperationDashLine } from "@/draws/DrawOperationDashLine";
import { DrawOperationWidthLine } from "@/draws/DrawOperationWidthLine";

class DrawManager extends BaseManager {
  private m_modelLayer: MLayer;
  private m_auxModel: MLayer;
  private m_signModel: MLayer;
  private m_eventManager: EventManager;
  private m_selectionManager: SelectionManager;
  private drawObject:
    | IDrawOperation
    | undefined
    | DrawOperationCircle
    | DrawOperationArc
    | DrawOperationLine
    | DrawLayout
    | DrawOperationWall;
  private m_positions: THREE.Vector3[] = [];
  private curpositon:
    | {
        point: THREE.Vector3;
        geo: any;
        position: IndsertPosition;
        obejct: DBEntity[] | THREE.Object3D[];
      }
    | undefined;
  private m_model: ModelManager;
  private m_view: ViewManager;
  private m_completeCallback:
    | ((entity: DBEntity | DBEntity[]) => void)
    | undefined
    | any;
  private onZoomBind: ((camera: THREE.Camera) => void) | undefined;
  private onMouseMoveBind: ((e: PointerEvent) => void) | undefined;
  private onMouseUpBind: ((e: PointerEvent) => void) | undefined;
  private onMouseDownBind: ((e: PointerEvent) => void) | undefined;
  private onKeyDownBind: ((e: KeyboardEvent) => void) | undefined;
  private onKeyUpBind: ((e: KeyboardEvent) => void) | undefined;
  private onWheelBind: ((e: WheelEvent) => void) | undefined;
  private onSnapFilterBind:
    | ((e: DrawOperationMovePointBeforeParams) => void)
    | undefined;
  private onInsertCallbackBind: ((e: IndserObject) => void) | undefined;
  public isEnableQuadrature: boolean = false;
  constructor(context: Context) {
    super(context);
    this.m_modelLayer = this.m_context.SceneManager.ModelLayer;
    this.m_model = this.m_context.ModelManager;
    this.m_auxModel = this.m_context.SceneManager.AuxModel;
    this.m_signModel = this.m_context.SceneManager.SignModel;
    this.m_eventManager = new EventManager();
    this.m_selectionManager = this.m_context.SelectionManager;
    this.m_view = context.ViewManager;
  }

  private initEvent() {
    this.onMouseMoveBind = this.onMouseMove.bind(this);
    this.m_context.ViewManager.EventManager.addEventListener(
      "pointermove",
      this.onMouseMoveBind
    );

    this.onMouseUpBind = this.onMouseUp.bind(this);
    this.m_context.ViewManager.EventManager.addEventListener(
      "pointerup",
      this.onMouseUpBind
    );

    this.onMouseDownBind = this.onMouseDown.bind(this);
    this.m_context.ViewManager.EventManager.addEventListener(
      "pointerdown",
      this.onMouseDownBind
    );

    this.onKeyDownBind = this.onKeyDown.bind(this);
    window.addEventListener("keydown", this.onKeyDownBind);

    this.onKeyUpBind = this.onKeyUp.bind(this);
    window.addEventListener("keyup", this.onKeyUpBind);

    this.onWheelBind = this.onWheel?.bind(this);
    window.addEventListener("wheel", this.onWheelBind);

    this.onSnapFilterBind = this.onSnapFilter.bind(this);
    this.m_selectionManager.EventManager.addEventListener(
      Selection_Event.snapFilter,
      this.onSnapFilterBind
    );

    this.onInsertCallbackBind = this.onInsertCallback.bind(this);
    this.m_selectionManager.EventManager.addEventListener(
      Selection_Event.inserts,
      this.onInsertCallbackBind
    );

    this.onZoomBind = this.OnZoom.bind(this);
    this.m_context.ViewManager.Controls.addEventListener(
      "zoom",
      this.onZoomBind
    );
  }
  private unInitEvent() {
    if (this.onMouseMoveBind)
      this.m_context.ViewManager.EventManager.removeEventListener(
        "pointermove",
        this.onMouseMoveBind
      );
    if (this.onMouseUpBind)
      this.m_context.ViewManager.EventManager.removeEventListener(
        "pointerup",
        this.onMouseUpBind
      );
    if (this.onMouseDownBind)
      this.m_context.ViewManager.EventManager.removeEventListener(
        "pointerdown",
        this.onMouseDownBind
      );
    if (this.onKeyUpBind) window.removeEventListener("keyup", this.onKeyUpBind);

    if (this.onKeyDownBind)
      window.removeEventListener("keydown", this.onKeyDownBind);
    if (this.onSnapFilterBind)
      this.m_selectionManager.EventManager.removeEventListener(
        Selection_Event.snapFilter,
        this.onSnapFilterBind
      );
    if (this.onInsertCallbackBind)
      this.m_selectionManager.EventManager.removeEventListener(
        Selection_Event.inserts,
        this.onInsertCallbackBind
      );
    if (this.onZoomBind)
      this.m_context.ViewManager.Controls.removeEventListener(
        "zoom",
        this.onZoomBind
      );
    if (this.onWheelBind) {
      window.removeEventListener("wheel", this.onWheelBind);
    }
  }
  public isEnableSnap: boolean = true;
  private DrawInit() {
    this.m_selectionManager.EnableSnap(this.isEnableSnap);
    this.m_selectionManager.HighLighting = false;
    this.m_context.ViewManager.Controls.MouseLeftPan = false;
    this.initEvent();
  }
  private DrawEnd() {
    this.isEnableQuadrature = false;
    this.isEnableSnap = true;
    this.m_context.Dom.style.cursor = "default";
    this.m_selectionManager.EnableSnap(false);
    this.m_selectionManager.HighLighting = true;
    this.m_context.ViewManager.Controls.MouseLeftPan = true;
    this.unInitEvent();
    this.m_view.Render();
  }
  private OnZoom(camera: THREE.Camera) {
    this.drawObject?.OnZoom(camera);
  }
  private onWheel(e: WheelEvent) {
    this.drawObject?.OnWheel(e);
  }
  private onKeyDown(e: KeyboardEvent) {
    this.drawObject?.onKeyDown(e);
  }

  private onKeyUp(e: KeyboardEvent) {
    this.drawObject?.onKeyUp(e);
  }
  private onMouseMove(e: PointerEvent) {
    this.drawObject?.onMouseMove(e);
  }
  private onMouseUp(e: PointerEvent) {}
  private onMouseDown(e: PointerEvent) {
    if (this.curpositon && e.button == 0) {
      // if (this.drawObject instanceof DrawOperationCircle) {
      //   if (this.drawObject.Radius) {
      //     this.DrawCircleFinish(DrawOperateState.complete);
      //     return;
      //   }
      // }
      // if (this.drawObject instanceof DrawOperationArc) {
      //   if (this.drawObject.Points.length === 2) {
      //     this.DrawArcFinish(DrawOperateState.complete);
      //     return;
      //   }
      // }
      // if (this.drawObject instanceof DrawOperationEllipse) {
      //   if (this.drawObject.Points.length === 2) {
      //     this.DrawEllipseFinish(DrawOperateState.complete);
      //     return;
      //   }
      // }
      // if (this.drawObject instanceof DrawOperationEllipseArc) {
      //   if (
      //     this.drawObject.Points.length === 2 &&
      //     !this.drawObject.isDrawEllipseEnd
      //   ) {
      //     this.drawObject.finishEllipse();
      //     return;
      //   }
      //   if (
      //     this.drawObject.isDrawEllipseEnd &&
      //     this.drawObject.isUpdateEllipseArc
      //   ) {
      //     this.DrawEllipseFinishArc(DrawOperateState.complete);
      //     return;
      //   }
      // }
      // if (this.drawObject instanceof DrawOperationRectangle) {
      //   if (this.drawObject.PS.length === 5) {
      //     this.DrawRectangleFinish(DrawOperateState.complete);
      //     return;
      //   }
      // }
      this.drawObject?.OnDownPoint({
        point: this.curpositon!.point,
        geo: this.curpositon!.geo,
        position: this.curpositon!.position,
        obejct: this.curpositon!.obejct,
      });
    }
  }

  /**
   * move时候触发，未交到直接使用loc，为未矫正的坐标
   * @param insert
   */
  public onInsertCallback(insert: IndserObject) {
    let p = insert.adjust ?? insert.location;
    this.curpositon = {
      point: new THREE.Vector3(p.x, p.y, p.z),
      geo: insert.geo,
      position: insert.position,
      obejct: insert.obejct,
    };
    this.drawObject?.OnMovePoint(this.curpositon);
  }
  private onSnapFilter(param: DrawOperationMovePointBeforeParams) {
    if (this.drawObject instanceof DrawLayout) {
      this.drawObject.drawLayoutObject.OnMovePointBefore(param);
    } else {
      this.drawObject?.OnMovePointBefore(param);
    }
  }

  /**
   * 绘制线
   * @param layerId 图层id
   * @param completeCallback 绘制结束回调
   * @param completeBeforeCallback 绘制结束前回调
   * @param isClose 是否闭合
   * @param isSubsection 是否分段
   * @param arraySignData 阵列标记数据
   * @param expandParameter 扩展参数
   * @returns
   */
  private isClose: boolean = false;
  private drawCount: number = 0;
  public DrawLine(
    layer: DbLayer,
    color: number,
    completeCallback: ((entity: DBEntity | DBEntity[]) => void) | undefined,
    callbackUnderway: (
      params: DrawOperationMovePointParams,
      points: THREE.Vector3[]
    ) => boolean,
    isClose: boolean = false
  ) {
    this.m_completeCallback = completeCallback;
    this.isClose = isClose;
    this.DrawInit();
    this.drawObject = new DrawOperationLine(this.m_context, {
      layer,
      color,
      callbackUnderway,
    });
  }

  public DrawCircle(
    layer: DbLayer,
    color: number,
    completeCallback: (params: {
      center: { x: number; y: number; z: number };
      radius: number;
    }) => void,
    canDrawCallback: (params: DrawOperationMovePointParams) => boolean
  ) {
    this.m_completeCallback = completeCallback;
    // let layer = this.m_model.GetLayerByName(layerName);
    // if (!layer) {
    //   layer = new DbLayer(layerName, generateUUID());
    //   layer.Color = CommonColor.White;
    //   layer.ColorIndex = 1;
    //   layer.Objects = [];
    //   this.m_model.addLayer(layer);
    // }
    this.DrawInit();

    this.drawObject = new DrawOperationCircle(
      {
        layer: layer,
        color,
        model: this.m_model,
        auxLayer: this.m_auxModel,
        view: this.m_context.ViewManager,
      },
      canDrawCallback
    );
    return this.drawObject;
  }

  public DrawArc(
    layer: DbLayer,
    color: number,
    completeCallback: (params: {
      center: { x: number; y: number; z: number };
      p1: { x: number; y: number; z: number };
      p2: { x: number; y: number; z: number };
      isAnticlockwise: boolean;
    }) => void,
    canDrawCallback: (params: DrawOperationMovePointParams) => boolean
  ) {
    this.m_completeCallback = completeCallback;
    // let layer = this.m_model.GetLayerByName(layerName);
    // if (!layer) {
    //   layer = new DbLayer(layerName, generateUUID());
    //   layer.Color = CommonColor.White;
    //   layer.ColorIndex = 1;
    //   layer.Objects = [];
    //   this.m_model.addLayer(layer);
    // }

    this.drawObject = new DrawOperationArc(
      {
        layer: layer,
        color,
        model: this.m_model,
        auxLayer: this.m_auxModel,
        view: this.m_context.ViewManager,
      },
      canDrawCallback
    );
    this.DrawInit();
  }

  public DrawEllipse(
    layer: DbLayer,
    color: number,
    completeCallback: (params: EllipseReturnData) => void,
    canDrawCallback: (params: DrawOperationMovePointParams) => boolean
  ) {
    this.m_completeCallback = completeCallback;
    // let layer = this.m_model.GetLayerByName(layerName);
    // if (!layer) {
    //   layer = new DbLayer(layerName, generateUUID());
    //   layer.Color = CommonColor.White;
    //   layer.ColorIndex = 1;
    //   layer.Objects = [];
    //   this.m_model.addLayer(layer);
    // }
    this.DrawInit();

    this.drawObject = new DrawOperationEllipse(
      {
        layer: layer,
        color,
        model: this.m_model,
        auxLayer: this.m_auxModel,
        view: this.m_context.ViewManager,
      },
      canDrawCallback
    );
    return this.drawObject;
  }

  public DrawEllipseArc(
    layer: DbLayer,
    color: number,
    completeCallback: (params: EllipseReturnData) => void,
    canDrawCallback: (params: DrawOperationMovePointParams) => boolean
  ) {
    this.m_completeCallback = completeCallback;
    // let layer = this.m_model.GetLayerByName(layerName);
    // if (!layer) {
    //   layer = new DbLayer(layerName, generateUUID());
    //   layer.Color = CommonColor.White;
    //   layer.ColorIndex = 1;
    //   layer.Objects = [];
    //   this.m_model.addLayer(layer);
    // }
    this.DrawInit();

    this.drawObject = new DrawOperationEllipseArc(
      {
        layer: layer,
        color,
        model: this.m_model,
        auxLayer: this.m_auxModel,
        view: this.m_context.ViewManager,
      },
      canDrawCallback
    );
    return this.drawObject;
  }

  // 绘制布置
  public DrawLayout(DrawLayoutParams: DrawLayoutParams) {
    if (!DrawLayoutParams.layoutType) {
      console.error("layoutType is not null");
      this.DrawEnd();
      return;
    }
    this.drawObject = new DrawLayout(this.m_context, DrawLayoutParams);
    this.DrawInit();
  }

  public DrawRectangle(
    layer: DbLayer,
    color: number,
    completeCallback: ((entity: DBEntity | DBEntity[]) => void) | undefined,
    isDashed: boolean = false
  ) {
    this.m_completeCallback = completeCallback;
    // let layer = this.m_model.GetLayerByName(layerName);
    // if (!layer) {
    //   layer = new DbLayer(layerName, generateUUID());
    //   layer.Color = CommonColor.White;
    //   layer.ColorIndex = 1;
    //   layer.Objects = [];
    //   this.m_model.addLayer(layer);
    // }
    this.DrawInit();

    this.drawObject = new DrawOperationRectangle(
      {
        layer: layer,
        color,
        model: this.m_model,
        auxLayer: this.m_auxModel,
        view: this.m_context.ViewManager,
      },
      isDashed
    );
  }
  // 绘制虚线
  public DrawDashLine(
    layer: DbLayer,
    color: number,
    completeCallback: ((entity: DBEntity | DBEntity[]) => void) | undefined,
    callbackUnderway: (point: THREE.Vector3) => void
  ) {
    this.m_completeCallback = completeCallback;
    this.DrawInit();
    this.drawObject = new DrawOperationDashLine(this.m_context, {
      layer,
      color,
      callbackUnderway,
    });
  }

  // 绘制粗线
  public DrawWidthLine(params: {
    layer: DbLayer;
    color: number;
    width: number;
    isDashed: boolean;
    completeCallback: ((entity: DBEntity | DBEntity[]) => void) | undefined;
    callbackUnderway: (point: THREE.Vector3) => void;
  }) {
    let { layer, color, width, isDashed, completeCallback, callbackUnderway } =
      params;
    this.m_completeCallback = completeCallback;
    this.DrawInit();
    this.drawObject = new DrawOperationWidthLine(this.m_context, {
      layer,
      color,
      width,
      isDashed,
      callbackUnderway,
    });
  }

  // 绘制墙
  private DrawWall(params: DrawWallAcceptParams) {
    this.drawObject = new DrawOperationWall(this.m_context, params);
    this.m_completeCallback = params.callBackFinish;
    this.DrawInit();
  }

  public DrawFinish(operate: DrawOperateState) {
    let dbentity = this.drawObject?.Finish(operate);
    if (dbentity && this.m_completeCallback) {
      this.m_completeCallback(dbentity as DBEntity);
    }
    this.DrawEnd();
    this.m_completeCallback = undefined;
    this.drawObject = undefined;
  }
}

export { DrawManager };
